/**
 * Item 6 (Warren, 2026-10-02): a resident's charger open to guests with no
 * guest price, and "no paid guests yet" at building plugs.
 *
 * Pure decisions for three screens, so they can be tested without React:
 *
 *   - the resident's charger page: where the guest price is set, the banner
 *     shown when paying guests are being turned away, whether the Open to
 *     guests switch may be turned on, and whether a save carries the markup
 *   - the pre-session page: whether a guest refusal is the unpriced one
 *   - the station page: which stalls say "isn't open to guests yet", and the
 *     guest pricing badge without stalls nothing bills a markup at
 *
 * The server decides all of this (GET /my-chargers/:id guest_pricing and
 * guest_price_missing, start-preview guestAccess.detail, driver-start's
 * detail, /locations/driver/:id guest_price_missing). The fallbacks here
 * only cover an older Api, which never sends the fields.
 */

export type ChargerGuestPricing = 'home_guest_rate' | 'building_unavailable' | 'markup';

export interface GuestPricingFacts {
  guest_pricing?: string | null;
  guest_price_missing?: boolean | null;
  home_monetization?: { enrolled?: boolean } | null;
  created_via?: string | null;
  is_open_to_guests?: boolean | null;
}

/** Where this charger's guest price is set. */
export function chargerGuestPricing(c: GuestPricingFacts): ChargerGuestPricing {
  if (c.guest_pricing === 'home_guest_rate' || c.guest_pricing === 'building_unavailable' || c.guest_pricing === 'markup') {
    return c.guest_pricing;
  }
  // An older Api: home_monetization is set only at a home.
  if (c.home_monetization != null) return 'home_guest_rate';
  return c.created_via === 'resident_self_service' ? 'building_unavailable' : 'markup';
}

/** The charger page banner while paying guests are being turned away. */
export type GuestPriceBanner = 'home_set_price' | 'home_enroll' | 'building';

export function guestPriceBanner(c: GuestPricingFacts): GuestPriceBanner | null {
  if (c.guest_price_missing !== true) return null;
  const kind = chargerGuestPricing(c);
  if (kind === 'home_guest_rate') return c.home_monetization?.enrolled === true ? 'home_set_price' : 'home_enroll';
  if (kind === 'building_unavailable') return 'building';
  return null;
}

/** The charger page's hero pill about guests. 'turned_away' while the
 *  server says paying guests are being refused for want of a price, so the
 *  page never shows a healthy "Open to guests" over a banner saying they
 *  can't charge. */
export function heroGuestPill(c: GuestPricingFacts): 'open' | 'turned_away' | null {
  if (c.is_open_to_guests !== true) return null;
  return c.guest_price_missing === true ? 'turned_away' : 'open';
}

/** The Earnings page's empty-state and payout copy. A building resident's
 *  charger has no guest earnings to wait for and needs no payout setup; a
 *  home earns through its guest price; any other charger through its
 *  markup. Unknown (the charger not loaded yet) reads as before: markup. */
export type EarningsCopy = 'building' | 'home' | 'markup';

export function earningsCopy(c: GuestPricingFacts | null | undefined): EarningsCopy {
  if (!c) return 'markup';
  const kind = chargerGuestPricing(c);
  if (kind === 'building_unavailable') return 'building';
  return kind === 'home_guest_rate' ? 'home' : 'markup';
}

/** A building resident's closed charger cannot be opened to guests: the
 *  Api refuses it (409 BUILDING_GUEST_PRICING_UNAVAILABLE). One already
 *  open keeps its switch so the resident can close it. */
export function openToGuestsLocked(c: GuestPricingFacts): boolean {
  return chargerGuestPricing(c) === 'building_unavailable' && c.is_open_to_guests !== true;
}

/** Only a charger the resident did not register still has a markup that
 *  bills; every other save leaves the stored guest_markup_bps alone. */
export function saveCarriesMarkup(c: GuestPricingFacts): boolean {
  return chargerGuestPricing(c) === 'markup';
}

/** A start-preview guestAccess verdict, or a driver-start error. */
export interface RefusalFacts {
  reason?: string | null;
  code?: string | null;
  detail?: string | null;
}

/** The unpriced refusal: GUEST_ACCESS_DISABLED with detail PRICE_NOT_SET. */
export function isPriceNotSet(r: RefusalFacts | null | undefined): boolean {
  if (!r) return false;
  return (r.reason ?? r.code) === 'GUEST_ACCESS_DISABLED' && r.detail === 'PRICE_NOT_SET';
}

export interface StationStall {
  is_active?: boolean;
  access_mode?: string;
  guest_markup_bps?: number | null;
  guest_price_missing?: boolean;
}

/** How the station page says it: on each flagged stall, and once in the
 *  header when every active stall is flagged (a home's single charger).
 *  count is the flagged stalls, so the header can say "This charger" for
 *  one and "These chargers" for several. */
export function stationGuestNotice(stalls: readonly StationStall[]): {
  anyStall: boolean;
  everyStall: boolean;
  count: number;
} {
  const active = stalls.filter((s) => s.is_active !== false);
  const flagged = active.filter((s) => s.guest_price_missing === true).length;
  return { anyStall: flagged > 0, everyStall: active.length > 0 && flagged === active.length, count: flagged };
}

/** The header's "+X% guest pricing": the highest markup across guest
 *  stalls, never one where the guest is turned away. Null when no guest
 *  stall carries a markup. */
export function stationGuestMarkupBps(stalls: readonly StationStall[]): number | null {
  const guest = stalls.filter((s) => s.is_active !== false && s.access_mode === 'guest' && s.guest_price_missing !== true);
  if (guest.length === 0) return null;
  return guest.reduce<number>(
    (max, s) => Math.max(max, typeof s.guest_markup_bps === 'number' ? s.guest_markup_bps : 0),
    0,
  );
}
